import axios from 'axios'
import config from '../../../../../config/config'
import BaseService from '../../../services/BaseService'

const HOST = `${config.API_HOST}`

export type CalendarHealth = {
  configured: boolean
  service_account_email?: string | null
  bound_divisions: number
  synced_events: number
  timezone: string
  steps: string[]
  error?: string
}

export type CalendarEventInfo = {
  id?: string
  summary?: string
  htmlLink?: string
  start?: { dateTime?: string; timeZone?: string }
  end?: { dateTime?: string; timeZone?: string }
  status?: string
  mode?: string
  attendee?: string
  note?: string
  email_invite_sent?: boolean
  google_event_id?: string
  invite_blocked?: boolean
  send_updates?: string
}

export type CalendarSyncResult = {
  appointment_id: number
  patient_appointment: number
  patient_name: string
  division_id: number
  division_name: string
  division_calendar_id: string
  mehealth_event: CalendarEventInfo
  patient_event: CalendarEventInfo | null
  patient_email?: string | null
  has_patient_email?: boolean
  patient_calendar_id?: string
  attendees: string[]
  send_updates?: string
  domain_wide_delegation?: boolean
  warnings: string[]
  status_display?: string
  estimated_at?: string
}

export type DivisionCalendarInfo = {
  division_id: number
  division_name?: string
  calendar_id: string | null
  bound?: boolean
  summary?: string
  time_zone?: string
  sync_enabled?: boolean
  staff_emails?: string[]
  share_emails?: string[]
  is_public?: boolean
  links?: {
    subscribe_link: string
    embed_url: string
    ics_url: string
  }
  events?: number
}

export type CalendarEventRow = {
  id: number
  patient_appointment: number
  side: string
  calendar_id: string
  event_id: string | null
  attendee: string | null
  synced: boolean
  html_link: string | null
  error: string | null
  last_synced_at: string | null
}

const authHeaders = (apiToken?: string) =>
  apiToken ? { Authorization: `Token ${apiToken}` } : undefined

export const CalendarHealthAPI = {
  get: async ({ apiToken }: { apiToken?: string } = {}) => {
    const { data } = await axios.get(`${HOST}/apis/QUE/calendar/health/`, {
      headers: authHeaders(apiToken),
    })
    return data as CalendarHealth
  },
}

export const DivisionCalendarAPI = {
  get: async ({ divisionId, apiToken }: { divisionId: number | string; apiToken?: string }) => {
    const { data } = await axios.get(`${HOST}/apis/QUE/calendar/division/${divisionId}/`, {
      headers: authHeaders(apiToken),
    })
    return data as DivisionCalendarInfo
  },

  provision: async ({
    divisionId,
    apiToken,
    calendarId,
    staffEmails,
    syncEnabled,
    shareEmails,
    isPublic,
  }: {
    divisionId: number | string
    apiToken?: string
    calendarId?: string
    staffEmails?: string[]
    syncEnabled?: boolean
    shareEmails?: string[]
    isPublic?: boolean
  }) => {
    const { data } = await axios.post(
      `${HOST}/apis/QUE/calendar/division/${divisionId}/`,
      {
        calendar_id: calendarId,
        staff_emails: staffEmails ?? [],
        sync_enabled: syncEnabled ?? true,
        ...(shareEmails !== undefined ? { share_emails: shareEmails } : {}),
        ...(isPublic !== undefined ? { is_public: isPublic } : {}),
      },
      { headers: authHeaders(apiToken) },
    )
    return data as DivisionCalendarInfo
  },
}

export const AppointmentCalendarAPI = {
  sync: async ({
    appointmentId,
    apiToken,
    notifyPatient = true,
    patientCalendarId,
    force,
  }: {
    appointmentId: number
    apiToken?: string
    notifyPatient?: boolean
    patientCalendarId?: string
    force?: boolean
  }) => {
    const { data } = await axios.post(
      `${HOST}/apis/QUE/calendar/appointment/sync/`,
      {
        patient_appointment: appointmentId,
        notify_patient: notifyPatient,
        ...(patientCalendarId ? { patient_calendar_id: patientCalendarId } : {}),
        ...(force ? { force: true } : {}),
      },
      { headers: authHeaders(apiToken) },
    )
    return data as CalendarSyncResult
  },

  remove: async ({ appointmentId, apiToken }: { appointmentId: number; apiToken?: string }) => {
    const { data } = await axios.delete(`${HOST}/apis/QUE/calendar/appointment/sync/`, {
      headers: authHeaders(apiToken),
      data: { patient_appointment: appointmentId },
    })
    return data as { appointment_id: number; removed: { calendar_id: string; event_id: string }[]; errors: string[] }
  },

  events: async ({ appointmentId, apiToken }: { appointmentId: number; apiToken?: string }) => {
    const { data } = await axios.get(`${HOST}/apis/QUE/calendar/appointment/events/`, {
      params: { patient_appointment: appointmentId },
      headers: authHeaders(apiToken),
    })
    return data as { count: number; results: CalendarEventRow[] }
  },

  /** ดึง event ของหลาย นัดหมายใน call เดียว (backend รับ id คั่นด้วย ,) */
  eventsFor: async ({
    appointmentIds,
    apiToken,
  }: {
    appointmentIds: (number | string)[]
    apiToken?: string
  }) => {
    if (!appointmentIds.length) return { count: 0, results: [] }
    const { data } = await axios.get(`${HOST}/apis/QUE/calendar/appointment/events/`, {
      params: { patient_appointment: appointmentIds.join(',') },
      headers: authHeaders(apiToken),
    })
    return data as { count: number; results: CalendarEventRow[] }
  },

  /** ทุก event บนปฏิทินของ division (ใช้แสดงในหน้าตั้งค่า) */
  eventsByCalendar: async ({ calendarId, apiToken }: { calendarId: string; apiToken?: string }) => {
    const { data } = await axios.get(`${HOST}/apis/QUE/calendar/appointment/events/`, {
      params: { calendar_id: calendarId },
      headers: authHeaders(apiToken),
    })
    return data as { count: number; results: CalendarEventRow[] }
  },
}
